import { isMobileSafari } from "@/common/helpers/parseUserAgent";

export type FitImageResult = {
  dataUrl: string;
  width: number;
  height: number;
  originalWidth: number;
  originalHeight: number;
};

const applyCrossOriginIfNeeded = (img: HTMLImageElement, src: string) => {
  if (src.startsWith("http://") || src.startsWith("https://")) {
    img.crossOrigin = "anonymous";
  }
};

/**
 * Checks if a canvas has any transparent pixels
 */
const EDGE_IGNORE_PX = 2;

export const hasTransparentBackground = (context: CanvasRenderingContext2D, canvas: HTMLCanvasElement): boolean => {
  const { width, height } = canvas;
  if (width <= EDGE_IGNORE_PX * 2 || height <= EDGE_IGNORE_PX * 2) {
    return false;
  }

  const data = context.getImageData(0, 0, width, height).data;
  for (let y = EDGE_IGNORE_PX; y < height - EDGE_IGNORE_PX; y++) {
    for (let x = EDGE_IGNORE_PX; x < width - EDGE_IGNORE_PX; x++) {
      if (data[(y * width + x) * 4 + 3] < 255) {
        return true;
      }
    }
  }
  return false;
};

export const imageHasTransparentPixels = (img: HTMLImageElement, width: number, height: number): boolean => {
  if (!width || !height) {
    return false;
  }
  const SAMPLE_MAX_SIDE = 256;
  const scale = Math.min(1, SAMPLE_MAX_SIDE / Math.max(width, height));
  const sampleWidth = Math.max(1, Math.round(width * scale));
  const sampleHeight = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = sampleWidth;
  canvas.height = sampleHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return false;
  }
  ctx.drawImage(img, 0, 0, sampleWidth, sampleHeight);
  try {
    return hasTransparentBackground(ctx, canvas);
  } catch {
    return false;
  }
};

export const detectImageType = (url: string): string | null => {
  if (url.includes(".jpg") || url.includes(".jpeg")) {
    return "image/jpeg";
  }
  if (url.includes(".png")) {
    return "image/png";
  }
  if (url.includes(".webp")) {
    return "image/webp";
  }
  if (url.includes(".gif")) {
    return "image/gif";
  }
  if (url.includes(".avif")) {
    return "image/avif";
  }
  if (url.includes(".svg")) {
    return "image/svg";
  }
  return null;
};

export const getSizeInBite = (dataUrl: string) => {
  // eslint-disable-next-line zeffy-linter/no-base64-images
  const head = "data:image/png;base64,";
  return Math.round(((dataUrl.length - head.length) * 3) / 4);
};

/**
 * Fetches the actual MIME type from an image URL by making a HEAD request
 * Falls back to null if fetch fails (CORS, network issues, etc.)
 * Skips blob URLs since they're blocked by CSP
 */
export const detectMimeTypeFromUrl = async (url: string): Promise<string | null> => {
  // Skip blob URLs - they're blocked by CSP and don't contain MIME type info
  if (url.startsWith("blob:")) {
    return null;
  }

  try {
    const response = await fetch(url, { method: "HEAD" });
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.startsWith("image/")) {
      return contentType.split(";")[0]; // Remove charset if present
    }

    return null;
  } catch {
    // If fetch fails (CORS, network, etc), return null to fall back
    return null;
  }
};

export const isHeicFormat = (file: File) => {
  return (
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    file.name?.toLowerCase().endsWith(".heic") ||
    file.name?.toLowerCase().endsWith(".heif")
  );
};

export const MAX_LOCAL_IMAGE_SIDE = 2560;

const DOWNSCALE_SKIPPED_TYPES = new Set(["image/gif", "image/svg+xml"]);

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));

const isCanvasBlank = (context: CanvasRenderingContext2D, width: number, height: number) => {
  const data = context.getImageData(0, 0, width, height).data;
  for (let index = 3; index < data.length; index += 4) {
    if (data[index] !== 0) {
      return false;
    }
  }
  return true;
};

export const downscaleLocalImage = async (file: File, maxSide: number = MAX_LOCAL_IMAGE_SIDE): Promise<Blob | null> => {
  if (typeof createImageBitmap !== "function" || DOWNSCALE_SKIPPED_TYPES.has(file.type)) {
    return null;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const scale = maxSide / Math.max(bitmap.width, bitmap.height);
    if (scale >= 1) {
      bitmap.close();
      return null;
    }

    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      bitmap.close();
      return null;
    }

    context.imageSmoothingQuality = "high";
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    if (isCanvasBlank(context, width, height)) {
      return null;
    }

    const outputType = file.type === "image/png" || file.type === "image/webp" ? file.type : "image/jpeg";
    return await canvasToBlob(canvas, outputType, 0.92);
  } catch {
    return null;
  }
};

export const processFileForUpload = async (
  file: File,
  options: {
    onHeicError?: (file: File) => void;
    amplitudePayload?: Record<string, unknown>;
  }
) => {
  if (isHeicFormat(file) && !isMobileSafari()) {
    options.onHeicError?.(file);
  }

  const downscaledImage = await downscaleLocalImage(file);

  return URL.createObjectURL(downscaledImage ?? file);
};

export const detectImageAspectRatio = async (imageDataUrl: string): Promise<"square" | "portrait" | "landscape"> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    applyCrossOriginIfNeeded(img, imageDataUrl);
    img.onload = () => {
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      const tolerance = 0.05;

      if (Math.abs(aspectRatio - 1) < tolerance) {
        resolve("square");
      } else if (aspectRatio < 1) {
        resolve("portrait");
      } else {
        resolve("landscape");
      }
    };
    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };
    img.src = imageDataUrl;
  });
};

export const fitImageToSquare = async (imageDataUrl: string, targetSize: number = 800): Promise<FitImageResult> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    applyCrossOriginIfNeeded(img, imageDataUrl);
    img.onload = async () => {
      const originalWidth = img.naturalWidth;
      const originalHeight = img.naturalHeight;

      const aspectRatioType = await detectImageAspectRatio(imageDataUrl);
      const aspectRatio = originalWidth / originalHeight;

      const { fittedWidth, fittedHeight } =
        aspectRatioType === "portrait"
          ? { fittedWidth: targetSize, fittedHeight: targetSize / aspectRatio }
          : aspectRatioType === "landscape"
            ? { fittedWidth: targetSize * aspectRatio, fittedHeight: targetSize }
            : { fittedWidth: targetSize, fittedHeight: targetSize };

      const canvas = document.createElement("canvas");
      canvas.width = fittedWidth;
      canvas.height = fittedHeight;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, fittedWidth, fittedHeight);

      const mimeType = imageDataUrl.split(";")[0].split(":")[1] || "image/png";

      resolve({
        dataUrl: canvas.toDataURL(mimeType, 0.9),
        width: fittedWidth,
        height: fittedHeight,
        originalWidth,
        originalHeight,
      });
    };
    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };
    img.src = imageDataUrl;
  });
};

export const generateSquareCrop = async (
  fittedImageDataUrl: string,
  fittedWidth: number,
  fittedHeight: number,
  dragOffset: { x: number; y: number },
  targetSize: number,
  outputSize?: number
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    applyCrossOriginIfNeeded(img, fittedImageDataUrl);
    img.onload = () => {
      const finalOutputSize = outputSize ?? targetSize;

      const canvas = document.createElement("canvas");
      canvas.width = finalOutputSize;
      canvas.height = finalOutputSize;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.imageSmoothingQuality = "high";

      let sourceX = -dragOffset.x;
      let sourceY = -dragOffset.y;

      const maxSourceX = Math.max(0, fittedWidth - targetSize);
      const maxSourceY = Math.max(0, fittedHeight - targetSize);
      sourceX = Math.max(0, Math.min(sourceX, maxSourceX));
      sourceY = Math.max(0, Math.min(sourceY, maxSourceY));

      ctx.drawImage(img, sourceX, sourceY, targetSize, targetSize, 0, 0, finalOutputSize, finalOutputSize);

      const mimeType = fittedImageDataUrl.split(";")[0].split(":")[1] || "image/png";

      resolve(canvas.toDataURL(mimeType, 1.0));
    };
    img.onerror = () => {
      reject(new Error("Failed to load fitted image"));
    };
    img.src = fittedImageDataUrl;
  });
};

export const generateFitModeCrop = async (
  originalImageDataUrl: string,
  aspectRatio: "portrait" | "landscape",
  targetSize: number,
  preserveTransparency?: boolean,
  outputSize?: number,
  useWhiteBackground?: boolean
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    applyCrossOriginIfNeeded(img, originalImageDataUrl);
    img.onload = () => {
      const finalOutputSize = outputSize ?? targetSize;
      const originalWidth = img.naturalWidth;
      const originalHeight = img.naturalHeight;
      const originalAspect = originalWidth / originalHeight;

      const canvas = document.createElement("canvas");
      canvas.width = finalOutputSize;
      canvas.height = finalOutputSize;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.imageSmoothingQuality = "high";
      if (!preserveTransparency && !imageHasTransparentPixels(img, originalWidth, originalHeight)) {
        if (useWhiteBackground) {
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, finalOutputSize, finalOutputSize);
        } else {
          // Blurred background (cover mode)
          const backgroundMetrics =
            aspectRatio === "portrait"
              ? {
                  width: finalOutputSize,
                  height: finalOutputSize / originalAspect,
                  x: 0,
                  y: (finalOutputSize - finalOutputSize / originalAspect) / 2,
                }
              : {
                  width: finalOutputSize * originalAspect,
                  height: finalOutputSize,
                  x: (finalOutputSize - finalOutputSize * originalAspect) / 2,
                  y: 0,
                };

          ctx.filter = "blur(20px)";
          ctx.drawImage(
            img,
            backgroundMetrics.x,
            backgroundMetrics.y,
            backgroundMetrics.width,
            backgroundMetrics.height
          );
          ctx.filter = "none";
        }
      }

      // Step 2: Draw main image (contain mode, centered)
      const containMetrics =
        aspectRatio === "portrait"
          ? {
              width: finalOutputSize * originalAspect,
              height: finalOutputSize,
              x: (finalOutputSize - finalOutputSize * originalAspect) / 2,
              y: 0,
            }
          : {
              width: finalOutputSize,
              height: finalOutputSize / originalAspect,
              x: 0,
              y: (finalOutputSize - finalOutputSize / originalAspect) / 2,
            };

      ctx.drawImage(img, containMetrics.x, containMetrics.y, containMetrics.width, containMetrics.height);

      const mimeType = originalImageDataUrl.split(";")[0].split(":")[1] || "image/png";
      resolve(canvas.toDataURL(mimeType, 1.0));
    };
    img.onerror = () => {
      reject(new Error("Failed to load image for fit mode crop"));
    };
    img.src = originalImageDataUrl;
  });
};
